// @flow
import * as React from 'react'
import logError from 'mz-utils/logError'
import { FormattedMessage } from 'mz-intl'
import type { PaymentOption } from 'mz-sdk/services/creditcard/getCreditCards'
import { StripeError, ERROR_CODES } from './StripeError'
import Button from '../../core/Button'
import { initStripe, getConfigByCurrency } from './stripe'
import LoadingSpinner from '../../core/LoadingSpinner'
import Alert from '../../core/Alert'
import PaymentMethodsList from './PaymentMethodsList'
import FreePaymentMethod from './FreePaymentMethod'
import messages from './messages.intl'
import { TryAgainButtonContainer } from './elements'
import { isAndroid } from '../../utils'
import {
  usePaymentIntent,
  type Data as PaymentIntentData,
  type Actions as PaymentIntentActions,
} from './usePaymentIntent'
import {
  CARD_BRAND_MAP,
  PRODUCTION_STRIPE_CONFIGS,
  DEVELOPMENT_STRIPE_CONFIGS,
  PAY_WITH_INTENT,
  PAY_WITH_DIRECT_BILL,
  PAY_WITH_OPTION,
  PAY_WITH_SOURCE,
  PAY_FOR_FREE,
  REQUEST_BUTTON_APPLEPAY,
  REQUEST_BUTTON_OTHER,
  REQUEST_BUTTON_GOOGLEPAY,
} from './constants'

// Types
import type { NestFunc } from '../Field/Field'
import type { Meta } from 'mz-ui-kit/core/BaseField'
import type { PaymentMethodType } from './PaymentMethodsList'
import type {
  StripeObject,
  PaymentRequest,
  StripeSource,
  StripePaymentIntent,
  StripeConfig,
} from './stripe'

export type CardBrandType = $Values<typeof CARD_BRAND_MAP>

export type PaymentResult =
  | { type: typeof PAY_WITH_DIRECT_BILL, brand?: CardBrandType }
  | { type: typeof PAY_FOR_FREE, brand?: CardBrandType }
  | {
      type: typeof PAY_WITH_OPTION,
      option: PaymentOption,
      brand?: CardBrandType,
    }
  | {
      type: typeof PAY_WITH_SOURCE,
      source: StripeSource,
      origin: string,
      currency: string,
      save?: boolean,
      brand?: CardBrandType,
    }
  | {
      type: typeof PAY_WITH_INTENT,
      intent: StripePaymentIntent,
      origin: string,
      currency: string,
      save?: boolean,
      brand?: CardBrandType,
    }

export type CreatePaymentFunc = () => Promise<PaymentResult>

export type RenderButtonOptions = {|
  displayAmount?: string,
  onClick?: Function,
  loading?: boolean,
  disabled?: boolean,
  isFree?: boolean,
  confirmLabel?: React.Node,
|}

export type Props = {
  currency: string,
  searchId: string,
  tripId: string,
  originalCurrency?: string,
  displayAmount: string,
  onClick: (CreatePaymentFunc) => any,
  onChangeCardBrand?: (brand: CardBrandType) => void,
  stripeChargeAmount: number,
  renderExtraCardFields?: (props: Props, nest: NestFunc) => React.Node,
  renderButton: (options: RenderButtonOptions) => React.Node,
  renderBeforeButton?: () => React.Node,
  allowedPaymentOptionIds?: Array<number>,
  allowedPaymentMethods?: Array<PaymentMethodType>,
  shouldSecurePayment?: boolean,
  canSaveCreditCard?: boolean,
  loadExternalCards: () => Promise<PaymentOption[]>,
  loadExternalStripeSource: (cardId: string) => Promise<ExternalCardDescriptor>,
  hideCvcField?: boolean,
  disabled?: boolean,
  loading?: boolean,
  stripeConfigs?: Array<StripeConfig>,
  devMode?: boolean,
  confirmLabel?: React.Node,
  freePaymentLabel?: React.Node,
  postPayment?: boolean,
  paymentIntentInput?: Array<any>,
  shouldShowCurrencySupportMessage?: boolean,
}

export type ExternalCardDescriptor = {
  stripe_source_id: string,
  three_d_secure: 'required' | 'supported' | 'not_supported',
}

export type ExtendedPaymentRequest = {
  request: PaymentRequest,
  type:
    | typeof REQUEST_BUTTON_APPLEPAY
    | typeof REQUEST_BUTTON_GOOGLEPAY
    | typeof REQUEST_BUTTON_OTHER,
}

export type MethodProps = Props & {
  config: StripeConfig,
  stripe: StripeObject,
  paymentRequest: ?ExtendedPaymentRequest,
  paymentIntent: ?PaymentIntentData,
  paymentIntentActions: PaymentIntentActions,
  changeMeta?: (meta: Meta) => Meta,
}

export function getConfigByProps(props: Props) {
  const { stripeConfigs, currency, devMode } = props
  const activeConfigs = !stripeConfigs
    ? devMode
      ? DEVELOPMENT_STRIPE_CONFIGS
      : PRODUCTION_STRIPE_CONFIGS
    : stripeConfigs
  return getConfigByCurrency(activeConfigs, currency)
}

/**
 * Creates a part of the reservation data based on payment object
 * create by the button
 * @param  {PaymentResult} paymentObj
 * @return {Object}
 */
export function createPaymentReservationData(paymentObj: PaymentResult) {
  const reservationData = {}
  if (paymentObj.type === PAY_WITH_DIRECT_BILL) {
    reservationData.direct_bill = true
  } else if (paymentObj.type === PAY_WITH_SOURCE) {
    reservationData.card_source_id = paymentObj.source.id
    reservationData.save_card = !!paymentObj.save
  } else if (paymentObj.type === PAY_WITH_OPTION) {
    reservationData.stored_card_id = paymentObj.option.id
  } else if (paymentObj.type === PAY_WITH_INTENT) {
    const intentField =
      paymentObj.intent.object === 'setup_intent'
        ? 'setupintent_id'
        : 'paymentintent_id'

    reservationData[intentField] = paymentObj.intent.id
    reservationData.save_card = !!paymentObj.save
  }
  if (paymentObj.origin) {
    reservationData.stripe_origin = paymentObj.origin
  }
  if (paymentObj.currency) {
    reservationData.currency = paymentObj.currency
  }
  return reservationData
}

/**
 * Creates an ExtendedPaymentRequest object in case if the browser
 * allows to pay using one of the payment methods (Apple/Google Pay)
 *
 * @param {StripeObject} stripe
 * @param {StripeConfig} config
 * @param {string} currency
 * @param {number} stripeChargeAmount
 * @return {*}  {(Promise<ExtendedPaymentRequest | null>)}
 */
async function getUsablePaymentRequest(
  stripe: StripeObject,
  config: StripeConfig,
  currency: string,
  stripeChargeAmount: number
): Promise<ExtendedPaymentRequest | null> {
  const paymentRequest = stripe.paymentRequest({
    country: config.country,
    currency: currency.toLowerCase(),
    requestPayerName: false,
    requestPayerEmail: false,
    total: {
      label: 'Ground Transportation',
      amount: stripeChargeAmount,
    },
  })

  const allowedRequestData = await paymentRequest.canMakePayment()
  if (!allowedRequestData) {
    return null
  }

  const paymentButtonType = !allowedRequestData.applePay
    ? isAndroid()
      ? REQUEST_BUTTON_GOOGLEPAY
      : REQUEST_BUTTON_OTHER
    : REQUEST_BUTTON_APPLEPAY

  return {
    request: paymentRequest,
    type: paymentButtonType,
  }
}

function usePaymentButtonState(props: Props) {
  const activeAttemptRef = React.useRef<number>(0)
  const [state, setState] = React.useState<{
    stripe: ?StripeObject,
    paymentRequest: ?ExtendedPaymentRequest,
    initError: boolean,
    attempt: number,
  }>({
    stripe: null,
    paymentRequest: null,
    initError: false,
    attempt: 0,
  })

  const stripeConfig = React.useMemo(() => getConfigByProps(props), [
    state.attempt,
    props.stripeConfigs,
    props.currency,
    props.devMode,
  ])

  React.useEffect(() => {
    const currentAttempt = ++activeAttemptRef.current
    const safeSetState = (...args) =>
      currentAttempt === activeAttemptRef.current && setState(...args)

    async function doInitStripe() {
      try {
        const stripe = await initStripe(stripeConfig.apiKey)
        safeSetState((x) => ({
          ...x,
          stripe,
          paymentRequest: null,
          initError: false,
        }))
        return stripe
      } catch (e) {
        logError(
          new StripeError(ERROR_CODES.init_error, null, e?.message ?? '')
        )
        safeSetState((x) => ({ ...x, initError: true }))
        return null
      }
    }

    doInitStripe()
  }, [setState, stripeConfig, activeAttemptRef])

  React.useEffect(() => {
    async function doInitPaymentRequest() {
      if (!state.stripe) return

      try {
        const paymentRequest = await getUsablePaymentRequest(
          state.stripe,
          stripeConfig,
          props.currency,
          props.stripeChargeAmount
        )
        setState((x) => ({ ...x, paymentRequest }))
      } catch (e) {
        logError(
          new StripeError(ERROR_CODES.init_error, null, e?.message ?? '')
        )
      }
    }

    doInitPaymentRequest()
  }, [state.stripe, stripeConfig, props.currency, props.stripeChargeAmount])

  const retryInit = React.useCallback(() => {
    activeAttemptRef.current += 1
    setState({
      stripe: null,
      paymentRequest: null,
      initError: false,
      attempt: activeAttemptRef.current,
    })
  }, [activeAttemptRef, setState])

  return [
    {
      ...state,
      config: stripeConfig,
    },
    { retryInit },
  ]
}

/**
 * A button that provides a way for the user to select a payment option,
 * enter any specific payment details and when everything is ready - call
 * the click handler with a function that returns a Promise which resolves
 * with payment details object that could be used to make a reservation for
 * example.
 * @type {Function}
 */
export function PaymentButton(props: Props) {
  const [
    { initError, stripe, config, paymentRequest },
    { retryInit },
  ] = usePaymentButtonState(props)

  const [intentState, paymentIntentActions] = usePaymentIntent({
    stripe,
    searchId: props.searchId,
    tripId: props.tripId,
    amount: props.stripeChargeAmount,
    currency: props.currency,
    stripeSource: config.origin,
    postPayment: props.postPayment,
    paymentIntentInput: props.paymentIntentInput,
  })

  if (!stripe && !initError) {
    return <LoadingSpinner />
  }

  if (initError || !stripe || !config) {
    return (
      <Alert color="red">
        <FormattedMessage message={messages.INIT_STRIPE_ERROR} />
        <TryAgainButtonContainer>
          <Button color="red" onClick={retryInit}>
            <FormattedMessage message={messages.RETRY_STRIPE_INIT} />
          </Button>
        </TryAgainButtonContainer>
      </Alert>
    )
  }

  const methodProps = {
    ...props,
    loading: props.loading || intentState.loading,
    paymentIntentActions,
    paymentIntent: intentState.data,
    paymentRequest,
    stripe,
    config,
  }

  return props.stripeChargeAmount === 0 ? (
    <FreePaymentMethod {...methodProps} />
  ) : (
    <PaymentMethodsList {...methodProps} />
  )
}

PaymentButton.defaultProps = {
  renderButton: (options: RenderButtonOptions) => {
    const buttonProps = {
      onClick: options.onClick,
      loading: options.loading,
      disabled: options.disabled,
    }

    const buttonContent = options.isFree ? (
      <FormattedMessage message={messages.BOOK_FOR_FREE} />
    ) : (
      options.confirmLabel || (
        <FormattedMessage
          message={messages.CONFIRM_BOOKING}
          values={{ price: options.displayAmount }}
        />
      )
    )

    return (
      <Button size="medium" color="cta" {...buttonProps}>
        {buttonContent}
      </Button>
    )
  },
}

export { StripeError }
export default PaymentButton
